import type { FormatedProductPrice } from "@dransay/api";
import { useCallback } from "react";
import { useLocation } from "react-router";
import { IntersectionObserverComponent } from "~/components/intersection-observer.component";
import { calculateProductsComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { ProductCard } from "~/features/products/card/product-card.component";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useMarkFavorites } from "~/hooks/use-mark-favorites.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	promotionClickedEvent,
	promotionViewedEvent,
} from "~/tooling/telemetry/events";
import { getProductDetailsUrl } from "~/utils/routes";

const PROMOTION_ID = "recommendations-slider-productPage";
const PROMOTION_NAME = "Recommendations Slider";

interface ExclusiveProductCardItemProps {
	product: FormatedProductPrice;
	index: number;
	className?: string;
}

export function ExclusiveProductCardItem({
	product,
	index,
	className,
}: ExclusiveProductCardItemProps) {
	const location = useLocation();
	const currency = useCurrency();
	const { isFavorite } = useMarkFavorites();
	const productUrl = (() => {
		const urlBase = getProductDetailsUrl(
			product.id,
			product.name,
			product.kind,
		);
		const queryParams = new URLSearchParams(location.search);
		queryParams.delete("search");
		const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
		return `${urlBase}${qs}`;
	})();
	const priceComparisonDiscount = calculateProductsComparisonDiscount(
		product.priceComparisonSet?.original,
		product.priceComparisonSet?.comparison,
	);
	const handleClick = useCallback(() => {
		captureEvent(
			promotionClickedEvent({
				promotionId: PROMOTION_ID,
				name: PROMOTION_NAME,
				position: index,
				products: [product],
				currency,
			}),
		);
	}, [currency, index, product]);
	const handleIntersection = useCallback(() => {
		captureEvent(
			promotionViewedEvent({
				promotionId: PROMOTION_ID,
				name: PROMOTION_NAME,
				position: index,
				products: [product],
				currency,
			}),
		);
	}, [currency, index, product]);
	return (
		<IntersectionObserverComponent onIntersection={handleIntersection}>
			<div className={className}>
				<ProductCard
					idSuffix="recommended"
					border="light"
					size="sm"
					variant="carouselItem"
					badges={["exclusive"]}
					forceExclusiveBadge
					exclusivePdpCard
					url={productUrl}
					priceComparisonDiscount={priceComparisonDiscount}
					listIndex={index}
					isFavoriteOverride={isFavorite(product.id)}
					onClick={handleClick}
					{...product}
				/>
			</div>
		</IntersectionObserverComponent>
	);
}
